// =============================================================
// BTR Companion — helpers de formatage / dérivations
// =============================================================

// Fenêtre d'analyse par défaut (jours). 30 = méta du patch courant.
// (Le DataCollector réalimente en continu des matchs récents 2026.)
export const WINDOW_DAYS = 30;

// RIOT team_position -> libellé de lane Companion
export const LANE_LABEL = {
  TOP: 'Top', JUNGLE: 'Jungle', MIDDLE: 'Mid', BOTTOM: 'ADC', UTILITY: 'Support',
};
export const laneLabel = (pos) => LANE_LABEL[pos] || pos || '—';
// Lane Companion -> team_position RIOT (filtre)
export const LANE_TO_POSITION = {
  Top: 'TOP', Jungle: 'JUNGLE', Mid: 'MIDDLE', ADC: 'BOTTOM', Support: 'UTILITY',
};

// Tier list dérivée du winrate + volume de parties.
export function deriveTier(winrate, games = 0) {
  if (!games || games < 30) return 'C';
  if (winrate >= 52) return 'S';
  if (winrate >= 50.5) return 'A';
  if (winrate >= 49) return 'B';
  if (winrate >= 47.5) return 'C';
  return 'D';
}

// Régions supportées (libellé Companion -> platform Riot + nom lisible)
export const REGIONS = [
  { code: 'EUW', platform: 'euw1', name: "Europe de l'Ouest" },
  { code: 'EUNE', platform: 'eun1', name: 'Europe Nord & Est' },
  { code: 'NA', platform: 'na1', name: 'Amérique du Nord' },
  { code: 'KR', platform: 'kr', name: 'Corée' },
  { code: 'BR', platform: 'br1', name: 'Brésil' },
  { code: 'JP', platform: 'jp1', name: 'Japon' },
  { code: 'LAN', platform: 'la1', name: 'Amérique latine Nord' },
  { code: 'LAS', platform: 'la2', name: 'Amérique latine Sud' },
  { code: 'OCE', platform: 'oc1', name: 'Océanie' },
  { code: 'TR', platform: 'tr1', name: 'Turquie' },
  { code: 'RU', platform: 'ru', name: 'Russie' },
];
export const REGION_CODES = REGIONS.map((r) => r.code);
export const regionName = (code) => REGIONS.find((r) => r.code === code)?.name || code;

// Région courante (partagée par la barre de recherche, Arena, Clash…)
export function loadRegion() {
  try { return localStorage.getItem('btr-region') || 'EUW'; } catch { return 'EUW'; }
}
export function saveRegion(r) {
  try { localStorage.setItem('btr-region', r); } catch { /* navigation privée */ }
}

// Classes ddragon (anglais) -> libellés FR
export const CLASS_FR = {
  Fighter: 'Combattant', Tank: 'Tank', Mage: 'Mage', Assassin: 'Assassin',
  Marksman: 'Tireur', Support: 'Soutien',
};
export const classFr = (t) => CLASS_FR[t] || t;

// Tags d'objets ddragon (anglais) -> indices FR lisibles (jeu Objet)
export const ITEM_TAG_FR = {
  Health: 'Vie', HealthRegen: 'Régénération de vie', Mana: 'Mana', ManaRegen: 'Régénération de mana',
  Armor: 'Armure', SpellBlock: 'Résistance magique', Damage: "Dégâts d'attaque", SpellDamage: 'Puissance',
  CriticalStrike: 'Coup critique', AttackSpeed: "Vitesse d'attaque", LifeSteal: 'Vol de vie',
  SpellVamp: 'Vampirisme', CooldownReduction: 'Accélération de compétences', AbilityHaste: 'Accélération de compétences',
  NonbootsMovement: 'Vitesse de déplacement', Boots: 'Bottes', Active: 'Effet actif', Aura: 'Aura',
  Slow: 'Ralentissement', Tenacity: 'Ténacité', OnHit: 'Effets à l\'impact', ArmorPenetration: "Pénétration d'armure",
  MagicPenetration: 'Pénétration magique', MagicResist: 'Résistance magique', Vision: 'Vision', Stealth: 'Furtivité',
  GoldPer: 'Revenu en or', Lane: 'Objet de lane', Jungle: 'Objet de jungle', Consumable: 'Consommable', Trinket: 'Bibelot',
};
export const itemTagFr = (t) => ITEM_TAG_FR[t] || t;

// Normalisation pour les recherches : sans accents, sans ponctuation, minuscules
// ("Kog'Maw" -> "kogmaw", "Séraphine" -> "seraphine", "Nunu et Willump" -> "nunuetwillump").
export const normalize = (s) => String(s || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[^a-z0-9]/g, '');
export const matchesQuery = (name, q) => !q || normalize(name).includes(normalize(q));

export function timeAgo(ms) {
  if (!ms) return '';
  const diff = Date.now() - Number(ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `Il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Hier';
  if (d < 7) return `Il y a ${d} j`;
  const w = Math.floor(d / 7);
  return `Il y a ${w} sem`;
}

export function fmtDuration(sec) {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Abrège un grand nombre (148_000_000 -> "148M", 2_400_000 -> "2,4M")
export function fmtCompact(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return (v / 1e9).toFixed(1).replace('.', ',') + ' Md';
  if (v >= 1e6) return (v / 1e6).toFixed(1).replace('.', ',') + 'M';
  if (v >= 1e3) return Math.round(v / 1e3).toLocaleString('fr-FR') + 'K';
  return v.toLocaleString('fr-FR');
}

// Joli libellé de tier classé (CHALLENGER -> Challenger), traduit les paliers.
const TIER_FR = {
  IRON: 'Fer', BRONZE: 'Bronze', SILVER: 'Argent', GOLD: 'Or',
  PLATINUM: 'Platine', EMERALD: 'Émeraude', DIAMOND: 'Diamant',
  MASTER: 'Maître', GRANDMASTER: 'Grand Maître', CHALLENGER: 'Challenger',
};
export const tierFr = (t) => TIER_FR[String(t || '').toUpperCase()] || t || 'Non classé';

export const QUEUE_FR = {
  RANKED_SOLO_5x5: 'Solo / Duo',
  RANKED_FLEX_SR: 'Flexible',
};
export const queueFr = (q) => QUEUE_FR[q] || q;

// Platform Riot ("euw1") -> code affiché ("EUW")
export const platformCode = (p) => REGIONS.find((r) => r.platform === String(p || '').toLowerCase())?.code || String(p || '').toUpperCase();
